/**
 * VaultPage - Show purchased products
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/services/api'

export function VaultPage() {
  const navigate = useNavigate()
  const [purchases, setPurchases] = useState<any[]>([])
  const [freebies, setFreebies] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [gridSize, setGridSize] = useState<'large' | 'compact'>('large') // large = 2 cols, compact = 4 cols
  const [mediaFilter] = useState<'all' | 'image' | 'video' | 'voice_note'>('all')
  const [contentFilter, setContentFilter] = useState<'all' | 'purchases' | 'freebies'>('all')
  const [selectedVideo, setSelectedVideo] = useState<any>(null) // For video modal

  useEffect(() => {
    fetchVaultContent()
  }, [])

  const fetchVaultContent = async () => {
    try {
      const token = localStorage.getItem('auth_token') // Fan frontend uses 'auth_token'

      // Fetch both purchases and freebies in parallel
      const [purchasesRes, freebiesRes] = await Promise.all([
        fetch(`${api.baseURL}/api/purchases`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${api.baseURL}/api/freebie-unlocks`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ])

      if (purchasesRes.ok) {
        const purchasesData = await purchasesRes.json()
        setPurchases(purchasesData.data || [])
      }

      if (freebiesRes.ok) {
        const freebiesData = await freebiesRes.json()
        setFreebies(freebiesData.data || [])
      }
    } catch (error) {
      console.error('Failed to load vault content:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Combine and filter content
  const allContent = [
    ...purchases.map(p => ({ ...p, type: 'purchase', item: p.photo, date: p.purchased_at })),
    ...freebies.map(f => ({ ...f, type: 'freebie', item: f.freebie, date: f.unlocked_at }))
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  const filteredContent = allContent.filter(content => {
    // Filter by content type
    if (contentFilter === 'purchases' && content.type !== 'purchase') return false
    if (contentFilter === 'freebies' && content.type !== 'freebie') return false

    // Filter by media type
    if (mediaFilter === 'all') return true
    return content.item?.media_type === mediaFilter
  })

  // Format duration
  const formatDuration = (seconds: number | undefined) => {
    if (!seconds) return '0:00'
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.floor(seconds % 60)
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    }
    return `${m}:${String(s).padStart(2, '0')}`
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#ece6dd] border-t-[#6b7850] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your vault...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header with back button and grid toggle */}
        <div className="flex flex-col gap-4 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.history.back()}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label="Go back"
              >
                <svg className="w-6 h-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-gray-900">Your Vault 🛡️</h1>
            </div>
          </div>

          {/* Media type filter and controls */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            {/* Filter buttons */}
            <div className="flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setContentFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  contentFilter === 'all'
                    ? 'text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={contentFilter === 'all' ? {background: 'linear-gradient(135deg, #6b7850 0%, #5c5b44 100%)'} : undefined}
              >
                All ({allContent.length})
              </button>
              <button
                onClick={() => setContentFilter('purchases')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  contentFilter === 'purchases'
                    ? 'text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={contentFilter === 'purchases' ? {background: 'linear-gradient(135deg, #6b7850 0%, #5c5b44 100%)'} : undefined}
              >
                Purchases ({purchases.length})
              </button>
              {freebies.length > 0 && (
                <button
                  onClick={() => setContentFilter('freebies')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    contentFilter === 'freebies'
                      ? 'text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  style={contentFilter === 'freebies' ? {background: 'linear-gradient(135deg, #86efac 0%, #22c55e 100%)'} : undefined}
                >
                  Freebies ({freebies.length})
                </button>
              )}
              {/* Media type filters temporarily disabled */}
              {/* <button
                onClick={() => setMediaFilter('image')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  mediaFilter === 'image'
                    ? 'text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={mediaFilter === 'image' ? {background: 'linear-gradient(135deg, #6b7850 0%, #5c5b44 100%)'} : undefined}
              >
                <span>📷 Images</span>
              </button>
              <button
                onClick={() => setMediaFilter('video')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  mediaFilter === 'video'
                    ? 'text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={mediaFilter === 'video' ? {background: 'linear-gradient(135deg, #a78bfa 0%, #6b7850 100%)'} : undefined}
              >
                <span>🎬 Videos</span>
              </button>
              <button
                onClick={() => setMediaFilter('voice_note')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  mediaFilter === 'voice_note'
                    ? 'text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                style={mediaFilter === 'voice_note' ? {background: 'linear-gradient(135deg, #6b7850 0%, #a78bfa 100%)'} : undefined}
              >
                <span>🎵 Audio</span>
              </button> */}
            </div>

            <div className="flex items-center gap-3">
              {/* Wallet Button */}
              <button
                onClick={() => navigate('/wallet')}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2"
                style={{background: 'linear-gradient(135deg, #6b7850 0%, #5c5b44 100%)'}}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <span className="hidden sm:inline">Payment Methods</span>
                <span className="sm:hidden">Cards</span>
              </button>

              {/* Grid Size Toggle */}
              {allContent.length > 0 && (
                <div className="flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm">
                  <button
                    onClick={() => setGridSize('large')}
                    className={`p-2 rounded transition-all ${
                      gridSize === 'large'
                        ? 'text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    style={gridSize === 'large' ? {background: 'linear-gradient(135deg, #6b7850 0%, #5c5b44 100%)'} : undefined}
                    aria-label="Large grid"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setGridSize('compact')}
                    className={`p-2 rounded transition-all ${
                      gridSize === 'compact'
                        ? 'text-white'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    style={gridSize === 'compact' ? {background: 'linear-gradient(135deg, #6b7850 0%, #5c5b44 100%)'} : undefined}
                    aria-label="Compact grid"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2zM9 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM19 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6z" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {filteredContent.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="w-20 h-20 bg-[#ece6dd] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">🛡️</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {allContent.length === 0 ? 'No content yet' : 'No items in this category'}
            </h2>
            <p className="text-gray-600">
              {allContent.length === 0 ? 'Purchased products and unlocked freebies will appear here' : 'Try selecting a different filter'}
            </p>
          </div>
        ) : (
          <div className={`grid ${
            gridSize === 'large'
              ? 'gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
              : 'gap-2 sm:gap-3 grid-cols-3 sm:grid-cols-4 lg:grid-cols-6'
          }`}>
            {filteredContent.map((content: any) => {
              const mediaType = content.item?.media_type || 'image'
              const isFreebie = content.type === 'freebie'
              const compact = gridSize === 'compact'

              return (
                <div key={content.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow">
                  {/* 9:16 portrait tile (measured: nearly every product is 1152x2048) on a black
                      ground, so the few landscape or square items get bars instead of a crop */}
                  <div className="aspect-[9/16] bg-black relative" data-vault-tile>
                    {/* Image */}
                    {mediaType === 'image' && content.item?.image_url ? (
                      <img
                        src={content.item.image_url}
                        alt={content.item.title}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          console.error('Failed to load image:', content.item?.title)
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    ) : mediaType === 'video' ? (
                      /* Video Preview - CLICKABLE */
                      <div
                        className="w-full h-full relative cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setSelectedVideo(content.item)}
                      >
                        <img
                          src={content.item?.thumbnail_url || content.item?.image_url}
                          alt={content.item?.title}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            console.error('Failed to load video thumbnail:', content.item?.title)
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 transition-all hover:bg-black/40">
                          <div className={`bg-white/90 rounded-full transform transition-transform hover:scale-110 ${compact ? 'p-2' : 'p-4'}`}>
                            <svg className={`text-braydin-olive ${compact ? 'w-5 h-5' : 'w-8 h-8'}`} fill="currentColor" viewBox="0 0 20 20">
                              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                            </svg>
                          </div>
                        </div>
                        {content.item?.duration_seconds && (
                          <div className={`absolute bg-black/80 text-white rounded font-semibold ${compact ? 'top-1 right-1 px-1 py-0.5 text-[10px]' : 'top-2 right-2 px-2 py-1 text-xs'}`}>
                            {formatDuration(content.item.duration_seconds)}
                          </div>
                        )}
                      </div>
                    ) : mediaType === 'voice_note' ? (
                      /* Audio Visual with Player */
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-[#ece6dd] to-braydin-sand2 p-4">
                        {/* Microphone icon */}
                        <div className="bg-gradient-to-br from-[#6b7850] to-braydin-olive rounded-full p-5 mb-4">
                          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
                            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                          </svg>
                        </div>
                        {/* Static waveform */}
                        <div className="flex items-center justify-center gap-[3px] h-10 mb-4">
                          {[...Array(25)].map((_, i) => (
                            <div
                              key={i}
                              className="w-[4px] rounded-full"
                              style={{
                                height: `${25 + Math.sin(i * 0.4) * 40 + (i % 3) * 12}%`,
                                background: 'linear-gradient(to top, #6b7850, #a855f7)'
                              }}
                            />
                          ))}
                        </div>
                        {/* Duration */}
                        {content.item?.duration_seconds && (
                          <div className="text-sm font-semibold text-braydin-oliveDark mb-3">
                            {formatDuration(content.item.duration_seconds)}
                          </div>
                        )}
                        {/* Audio player */}
                        {content.item?.image_url && (
                          <audio
                            controls
                            controlsList="nodownload"
                            className="w-full max-w-[200px] h-9"
                            style={{ filter: 'hue-rotate(320deg)' }}
                          >
                            <source src={content.item.image_url} type="audio/mpeg" />
                            Your browser does not support the audio element.
                          </audio>
                        )}
                      </div>
                    ) : (
                      /* Fallback */
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-12 h-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}

                    {/* Media type badge */}
                    <div className={`absolute font-semibold text-white ${compact ? 'top-1 left-1 px-1.5 py-0.5 rounded text-[10px]' : 'top-2 left-2 px-2 py-1 rounded-lg text-xs'}`}
                      style={{
                        background: mediaType === 'video'
                          ? 'linear-gradient(135deg, #a78bfa 0%, #6b7850 100%)'
                          : mediaType === 'voice_note'
                          ? 'linear-gradient(135deg, #6b7850 0%, #a78bfa 100%)'
                          : 'linear-gradient(135deg, #6b7850 0%, #5c5b44 100%)'
                      }}
                    >
                      {/* Drawn icons, not emoji: emoji render differently per device and look broken at badge size */}
                      <span className="inline-flex items-center gap-1">
                        {mediaType === 'video' ? (
                          <svg className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        ) : mediaType === 'voice_note' ? (
                          <svg className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                          </svg>
                        ) : (
                          <svg className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        )}
                        {!compact && <span>{mediaType === 'video' ? 'Video' : mediaType === 'voice_note' ? 'Audio' : 'Image'}</span>}
                      </span>
                    </div>
                  </div>
                  <div className={compact ? 'px-2 py-1.5' : 'p-3'}>
                    <div className={`text-gray-500 truncate ${compact ? 'text-[11px]' : 'text-xs'}`}>
                      <span>{isFreebie ? 'Unlocked' : 'Purchased'} {new Date(content.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Video Player Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedVideo(null)}
        >
          <div
            className="relative bg-black rounded-lg max-w-4xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Video title */}
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent p-4 rounded-t-lg z-10">
              <h3 className="text-white font-semibold text-lg">{selectedVideo.title}</h3>
              {selectedVideo.description && (
                <p className="text-gray-300 text-sm mt-1">{selectedVideo.description}</p>
              )}
            </div>

            {/* Video player */}
            <video
              controls
              controlsList="nodownload"
              autoPlay
              className="w-full rounded-lg"
              style={{ maxHeight: '80vh' }}
              poster={selectedVideo.thumbnail_url || selectedVideo.image_url}
            >
              <source src={selectedVideo.video_url || selectedVideo.image_url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}
    </div>
  )
}
